import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShieldAlert, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = new Map();

// Resolves the :editorId route param (friendly slug or legacy id) to { id, slug }
export const useEditorRef = () => {
  const { editorId: ref } = useParams();
  const [state, setState] = useState(() => (ref ? cache.get(ref) || null : { id: undefined, slug: undefined }));
  useEffect(() => {
    if (!ref) { setState({ id: undefined, slug: undefined }); return; }
    if (cache.has(ref)) { setState(cache.get(ref)); return; }
    setState(null);
    axios.get(`${API}/digital/resolve/${encodeURIComponent(ref)}`)
      .then(({ data }) => { cache.set(ref, data); cache.set(data.id, data); cache.set(data.slug, data); setState(data); })
      .catch(() => setState({ error: true }));
  }, [ref]);
  return state;
};

// A catalog suspended mid-session: drop cached refs and reload to show the suspension screen
export const reloadIfSuspended = (err) => {
  if (err?.response?.status !== 423) return false;
  cache.clear();
  window.location.reload();
  return true;
};

export const CatalogSuspended = ({ suspension }) => (
  <div data-testid="catalog-suspended" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', padding: '1.5rem' }}>
    <div style={{ maxWidth: '520px', width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '2.5rem 2rem', textAlign: 'center', color: '#e2e8f0' }}>
      <ShieldAlert className="w-14 h-14" style={{ color: '#f59e0b', margin: '0 auto 1rem' }} />
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.75rem', color: 'white' }}>{/teste gratuito/i.test(suspension.message || '') ? 'Teste gratuito encerrado' : 'Plataforma suspensa'}</h1>
      {suspension.nome && <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>{suspension.nome}</p>}
      <p data-testid="catalog-suspended-message" style={{ lineHeight: 1.6, marginBottom: '1.5rem' }}>
        {suspension.message || 'Esta plataforma foi suspensa por infringir as regras da plataforma Livrum. Para mais informações, entre em contato com o editor.'}
      </p>
      {suspension.email && (
        <a href={`mailto:${suspension.email}`} data-testid="catalog-suspended-email"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.7rem 1.2rem', borderRadius: '999px', background: 'rgba(104,170,176,0.15)', color: '#68AAB0', fontWeight: 600, textDecoration: 'none', wordBreak: 'break-all' }}>
          <Mail className="w-4 h-4" /> {suspension.email}
        </a>
      )}
      <img src="/livrum-logo-light.webp" alt="Livrum" style={{ height: '32px', width: 'auto', margin: '2rem auto 0', display: 'block', opacity: 0.8 }} />
    </div>
  </div>
);

export const withEditorRef = (Component) => function EditorRefWrapper(props) {
  const ref = useEditorRef();
  if (!ref) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}><Loader2 className="w-10 h-10 animate-spin" style={{ color: '#68AAB0' }} /></div>;
  }
  if (ref.error) {
    return <div data-testid="library-not-found" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#e2e8f0' }}>Biblioteca não encontrada</div>;
  }
  if (ref.suspension) return <CatalogSuspended suspension={ref.suspension} />;
  return <Component {...props} editorId={ref.id} editorSlug={ref.slug || ref.id} />;
};
